// Country codes
export const countryCodes = [
  { code: "+91", label: "India" },
  { code: "+66", label: "Thailand" },
];

// Toaster message severity
export type Severity = "error" | "warning" | "info" | "success";

// User roles
export enum EUserRoles {
  admin = "Admin",
  superAdmin = "Super Admin",
  agent = "Agent",
  manager = "Manager",
}

// App Constants
export enum EAppConstants {
  "APP_LANGUAGE" = "__AL__",
  "APP_AUTH_TOKEN_KEY" = "__AAT__",
  "APP_REFRESH_TOKEN_KEY" = "__ART__",
  "APP_SESSION_TOKEN_KEY" = "__AST__",
}

// User data
export interface IUser {
  name: string;
  role: string;
}

// Error Codes
export enum EErrorCodes {
  // Login error codes
  E_INVALID_CREDENTIALS = "E_INVALID_CREDENTIALS",

  // OTP error codes
  E_INVALID_OTP = "E_INVALID_OTP",
  E_OTP_EXPIRED = "E_OTP_EXPIRED",
  E_TOO_MANY_RESEND_ATTEMPTS = "E_TOO_MANY_RESEND_ATTEMPTS",

  // Account Setup / Reset Password
  E_UNKNOWN_NUMBER = "E_UNKNOWN_NUMBER",

  // Add client error codes
  E_DUPLICATE_ADMIN_USERNAME = "E_DUPLICATE_ADMIN_USERNAME",
  E_DUPLICATE_ADMIN_EMAIL = "E_DUPLICATE_ADMIN_EMAIL",
  E_DUPLICATE_ADMIN_PHONENUMBER = "E_DUPLICATE_ADMIN_PHONENUMBER",
  E_DUPLICATE_CLIENT_EMAIL = "E_DUPLICATE_CLIENT_EMAIL",
  E_DUPLICATE_CLIENT_PHONENUMBER = "E_DUPLICATE_CLIENT_PHONENUMBER",

  // Add user error codes
  E_DUPLICATE_USERNAME = "E_DUPLICATE_USERNAME",
  E_DUPLICATE_USER_EMAIL = "E_DUPLICATE_USER_EMAIL",
  E_DUPLICATE_USER_PHONENUMBER = "E_DUPLICATE_USER_PHONENUMBER",
  E_INVALID_ROLE_FOR_CLIENT = "E_INVALID_ROLE_FOR_CLIENT",

  // Publish tiny form
  E_FROM_ALREADY_PUBLISHED = "E_FROM_ALREADY_PUBLISHED",
  E_FORM_NOT_EDITABLE = "E_FORM_NOT_EDITABLE",

  // Pending request > approve/ reject field
  E_FIELD_NOT_FOUND = "E_FIELD_NOT_FOUND",
  E_FIELD_NOT_PENDING_APPROVAL = "E_FIELD_NOT_PENDING_APPROVAL",

  // Farmer error code
  E_FARMER_DUPLICATE_PHONENUMBER = "E_FARMER_DUPLICATE_PHONENUMBER",
  E_FARMER_DUPLICATE_NATIONAL_ID = "E_FARMER_DUPLICATE_NATIONAL_ID",
  E_FARMER_DUPLICATE_INTERNAL_ID = "E_FARMER_DUPLICATE_INTERNAL_ID",

  // Field error code
  E_FORM_RESPONSE_NOT_FOUND = "E_FORM_RESPONSE_NOT_FOUND",
  E_FIELD_DUPLICATE_FIELD_IDENTIFICATION_NUMBER = "E_FIELD_DUPLICATE_FIELD_IDENTIFICATION_NUMBER",
  E_FORM_NOT_FOUND = "E_FORM_NOT_FOUND",

  // AA-1731 — Bulk Shapefile import (upload / validate)
  E_SHAPEFILE_INVALID_ARCHIVE = "E_SHAPEFILE_INVALID_ARCHIVE",
  E_SHAPEFILE_MISSING_COMPONENTS = "E_SHAPEFILE_MISSING_COMPONENTS",
  E_SHAPEFILE_EMPTY = "E_SHAPEFILE_EMPTY",
  E_SHAPEFILE_MISSING_SEQID = "E_SHAPEFILE_MISSING_SEQID",
  E_SHAPEFILE_DUPLICATE_SEQID = "E_SHAPEFILE_DUPLICATE_SEQID",

  // AA-1731 — Bulk Shapefile import (batch apply)
  E_FIELD_IMPORT_BATCH_NOT_FOUND = "E_FIELD_IMPORT_BATCH_NOT_FOUND",
  E_FIELD_IMPORT_BATCH_NOT_PENDING = "E_FIELD_IMPORT_BATCH_NOT_PENDING",

  // AA-1747 — large-file (S3) bulk import: returned directly by the
  // /bulk/s3 call, or reported later on the batch's `errorSummary`
  E_FIELD_IMPORT_SOURCE_FILE_NOT_FOUND = "E_FIELD_IMPORT_SOURCE_FILE_NOT_FOUND",
  E_FIELD_IMPORT_SOURCE_FILE_INVALID = "E_FIELD_IMPORT_SOURCE_FILE_INVALID",
  E_FIELD_IMPORT_SOURCE_FILE_TOO_LARGE = "E_FIELD_IMPORT_SOURCE_FILE_TOO_LARGE",
  E_FIELD_IMPORT_ZIP_TOO_LARGE = "E_FIELD_IMPORT_ZIP_TOO_LARGE",
  E_FIELD_IMPORT_ZIP_TOO_MANY_ENTRIES = "E_FIELD_IMPORT_ZIP_TOO_MANY_ENTRIES",
  E_FIELD_IMPORT_STAGING_ERROR = "E_FIELD_IMPORT_STAGING_ERROR",
  E_FILESTORE_FILE_NOT_FOUND = "E_FILESTORE_FILE_NOT_FOUND",

  // Backend permission check failed (HTTP 200, status false)
  E_ACCESS_DENIED = "E_ACCESS_DENIED",

  // AA-1732 — EUDR Dashboard: a filter value (e.g. request_level) the
  // backend doesn't recognise
  E_DASHBOARD_INVALID_FILTER_VALUE = "E_DASHBOARD_INVALID_FILTER_VALUE",
}

// Dictionary data type
export interface IDictionary {
  [languageCode: string]: IGenericObject[];
}

// Generic key value object type
export interface IGenericObject {
  id: string;
  name: string;
}

// User Roles
export enum EUserRoles {
  ROLE_SUPERADMIN = "ROLE_SUPERADMIN",
  ROLE_ADMIN = "ROLE_ADMIN",
  ROLE_MANAGER = "ROLE_MANAGER",
  ROLE_SUPERVISOR = "ROLE_SUPERVISOR",
  ROLE_AGENT = "ROLE_AGENT",
}

// Client Tabs
export enum EClientTabs {
  ENTITY_CLIENT = "ENTITY_CLIENT",
  ENTITY_CROP_TYPE = "ENTITY_CROP_TYPE",
  ENTITY_LAND_TYPE = "ENTITY_LAND_TYPE",
  ENTITY_SPECIAL_LAND_PLOT = "ENTITY_SPECIAL_LAND_PLOT",
}

// Farmer tabs
export enum EFarmerTabs {
  ENTITY_FARMER = "ENTITY_FARMER",
  ENTITY_FIELD = "ENTITY_FIELD",
  ENTITY_CROP_CYCLE = "ENTITY_CROP_CYCLE",
  /**
   * Tier-1 Supplier ("Dealer") tab — sits alongside Farmer / Field / Crop Cycle
   * on the Farmer's Data page. Added in AA-1568 (Phase 2 / R1a).
   */
  ENTITY_DEALER = "ENTITY_DEALER",
}

// Compliance statuses for farmer, fields
export enum EComplianceStatus {
  PENDING_REVIEW = "PENDING",
  COMPLIANT = "COMPLIANT",
  NOT_COMPLIANT = "NOT_COMPLIANT",
}

// Field approval statuses
export enum EFieldApprovalStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

// Field overlap statuses
export enum EFieldOverlapStatus {
  PENDING = "PENDING",
  NO_OVERLAP = "NO_OVERLAP",
  OVERLAP = "OVERLAP",
}

// Crop status
export enum ECropStatus {
  PREPARE = "PREPARE",
  ONGOING = "ONGOING",
  HARVESTED = "HARVESTED",
}

// Compliance report enum
export enum EComplianceReportType {
  FIELD = "FIELD",
}
export interface IImportError {
  row: number;
  message: string;
  /** Optional. Emitted by the Dealer import backend
   *  (``DealerImportRowError.dealer_id``) so the error modal can show
   *  the dealer identifier each failing row carried — helpful when
   *  fixing a large CSV (AA-1568 AC #10). */
  dealerId?: string | null;
  /** Optional. Some imports include the backend error code so the
   *  modal can group / colour error categories in a follow-up pass. */
  code?: string | null;
}

// Form category
export enum EFormCategory {
  CROP_CYCLE_ACTIVITY = "CROP_CYCLE_ACTIVITY",
  FARMER_Q = "FARMER_Q",
  FIELD_Q = "FIELD_Q",
}
